import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { useSelector } from "react-redux";

import * as zh from "plotly.js/lib/locales/zh-cn";

import { XlsNormalizefileKey } from "../../utils/constantsString";
import {
  formatUnitForDisplay,
  getDerivedNwpUnitFromFlow,
} from "../../utils/Utils";

const ChartPlot = ({
  date,
  data,
  color,
  title,
  cips,
  i18n,
  label,
  threshold,
  replacement_date,
  upperLimitData,
  lowerLimitData,
  forecateDataX,
}) => {
  const permeate = useSelector((state) => state.permeate_quality);
  const flow = useSelector((state) => state.flow);
  const pressure = useSelector((state) => state.pressure);
  const temperature = useSelector((state) => state.temperature);
  const feed = useSelector((state) => state.feed_quality);

  const unitSelector = (name) => {
    name = String(name);
    try {
      if (name) {
        if (
          name === XlsNormalizefileKey.normalizeSaltPassage ||
          name === XlsNormalizefileKey.failureProbability
        ) {
          return "%";
        } else if (name === XlsNormalizefileKey.normalizeWaterPermeability) {
          return getDerivedNwpUnitFromFlow(flow);
        } else if (name.toLowerCase().includes("Flow".toLowerCase())) {
          return flow;
        } else if (
          name.toLowerCase().includes("Pressure".toLowerCase()) ||
          name.toLowerCase().includes("Norm. dp".toLowerCase())
        ) {
          return pressure;
        } else if (
          name.toLowerCase().includes("Conductivity".toLowerCase()) ||
          name.toLowerCase().includes("Quality".toLowerCase())
        ) {
          return name.toLowerCase().includes("Permeate".toLowerCase())
            ? formatUnitForDisplay(permeate)
            : formatUnitForDisplay(feed);
        } else if (name.toLowerCase().includes("TDS".toLowerCase())) {
          return "TDS";
        } else if (
          name.toLowerCase().includes("Temperature".toLowerCase()) ||
          name.toLowerCase().includes("Temp".toLowerCase())
        ) {
          return formatUnitForDisplay(temperature);
        }
      }
    } catch (err) {}
    return "";
  };

  const [plotData, setPlotData] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [minY, setMinY] = useState(null);
  const [maxY, setMaxY] = useState(null);
  const unit = unitSelector(label);
  const setNormalizePermeate = () => {
    const x = [];
    const y = [];
    const uppery = [];
    const lowery = [];
    for (const key in date) {
      x.push(date[key]);
      y.push(data[key]);
    }
    for (const key in forecateDataX) {
      uppery.push(upperLimitData[0][key]);
      lowery.push(lowerLimitData[0][key]);
    }

    // Create a fresh array instead of mutating state
    const newRowData = [];

    newRowData.push({
      x,
      y,
      mode: "markers",
      type: "scattergl",
      text: "",
      line: {
        color: color,
        width: 2,
      },
      marker: { color: color, size: 3 },
      hoverinfo: "x+y",
    });

    newRowData.push({
      x: forecateDataX,
      y: uppery,
      mode: "markers",
      type: "scattergl",
      text: "upper limit",
      marker: { color: "#5e5e5e", size: 2 },
      hoverinfo: "x+y",
    });
    newRowData.push({
      x: forecateDataX,
      y: lowery,
      mode: "markers",
      type: "scattergl",
      text: "lower limit",
      marker: { color: "#878787", size: 2 },
      hoverinfo: "x+y",
    });
    // Combine all y values for proper range calculation
    const numericValues = y.filter(
      (value) => typeof value === "number" && !isNaN(value),
    );

    const upperNumericValues = uppery.filter(
      (value) => typeof value === "number" && !isNaN(value),
    );
    const lowerNumericValues = lowery.filter(
      (value) => typeof value === "number" && !isNaN(value),
    );

    // Combine all values to get the complete data range
    const allValues = [
      ...(numericValues || []),
      ...(upperNumericValues || []),
      ...(lowerNumericValues || []),
    ];

    // Calculate min and max once and reuse
    const maxValue = allValues.reduce((a, b) => Math.max(a, b), -Infinity);
    const minValue = allValues.reduce((a, b) => Math.min(a, b), Infinity);

    if (
      threshold === undefined ||
      threshold.length <= 0 ||
      threshold[0] === null ||
      threshold[0] === undefined ||
      threshold[0] === ""
    ) {
      setMaxY(maxValue);
      setMinY(minValue);
    } else {
      if (maxValue > threshold[0]) {
        setMaxY(maxValue);
      } else {
        setMaxY(threshold[0]);
      }

      if (minValue < threshold[0]) {
        setMinY(minValue);
      } else {
        setMinY(threshold[0]);
      }
    }

    setRowData(newRowData);
    setPlotData(newRowData);
  };

  const layout = {
    yaxis: {
      ticks: "outside",
      linecolor: "#ccc",
      linewidth: 2,
      mirror: true,
      title: {
        text: unit,
        font: {
          size: 14,
        },
      },
    },
    xaxis: {
      type: "date",
      tickformat: "%d/%m/%Y",
      ticks: "outside",
      linecolor: "#ccc",
      linewidth: 2,
      mirror: true,
    },

    width: Math.round((90 / 100) * window.innerWidth),
    height: 120,
    margin: {
      b: 30,
      t: 30,
    },
    showlegend: false,
    font: {
      size: 12, // Set the font size
      color: "--color-primary-black", // Set the font color
    },
    hoverlabel: {
      font: {
        size: 14,
      },
    },
  };

  useEffect(() => {
    // Reset states when data changes to prevent stale data
    setRowData([]);
    setPlotData(null);
    setMinY(null);
    setMaxY(null);

    // Then set new data
    setNormalizePermeate();
  }, [
    data,
    date,
    forecateDataX,
    upperLimitData,
    lowerLimitData,
    threshold,
    color,
  ]);

  useEffect(() => {
    if (minY != null && maxY != null && rowData.length > 0) {
      const verticalTraces = [...rowData];

      if (cips) {
        cips.forEach((item) => {
          const verticalLineTrace = {
            x: [item.start_date, item.start_date],
            y: [minY, maxY],
            mode: "lines",
            hoverinfo: "x",
            line: {
              color: item.is_tool_identified ? "black" : "red",
              dash: "line",
              width: 1,
            },
          };
          verticalTraces.push(verticalLineTrace);
        });
      }

      if (replacement_date) {
        replacement_date.forEach((replacement) => {
          const replacementLineTrace = {
            x: [replacement, replacement],
            y: [minY, maxY],
            mode: "lines",
            hoverinfo: "x",
            line: {
              color: "red",
              dash: "line",
              width: 1,
            },
          };
          verticalTraces.push(replacementLineTrace);
        });
      }

      if (threshold) {
        // Create threshold line with same y-value for all x-axis dates
        // This enables continuous hover along the entire line
        // As we are getting date as object in cleaning advisor charts converting it to array
        const allDates = Object.values(date);
        const allYValues = allDates.map(() => threshold[0]);
        const horizontalLineTrace = {
          x: allDates,
          y: allYValues,
          mode: "lines",
          hoverinfo: "x+y",
          line: {
            color: "red",
            dash: "dot",
            width: 1,
          },
        };
        verticalTraces.push(horizontalLineTrace);
      }

      setPlotData(verticalTraces);
    }
  }, [rowData, cips, minY, maxY, threshold, replacement_date, date]);

  return (
    <Plot
      data={plotData}
      layout={{
        ...layout,
        title: {
          text: title + (unit ? " (" + unit + ")" : ""),
          font: {
            color: color,
            size: 14,
          },
        },
      }}
      config={{
        locales: { [i18n.language]: i18n.language === "zh" ? zh : "" },
        locale: i18n.language,
        responsive: true,
        toImageButtonOptions: {
          format: "png",
          filename: title,
          height: layout.height,
          width: layout.width,
          scale: 2,
        },
      }}
    ></Plot>
  );
};

export default ChartPlot;
